/**
 * Connections: one card per thing this service talks to.
 *
 * A connection's URL and its key are shown together and edited in one form.
 * On 2026-10-08 a key went into another connection's field because its own
 * had none, and on 2026-10-11 a key went into an address field that sat in a
 * different block from the key input. Both were this page's layout.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import {
  Body1Strong,
  Button,
  Caption1,
  Field,
  Input,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  Switch,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import {
  KeyValueList,
  KeyValueRow,
  MonoText,
  SectionCard,
  SectionLabel,
  StatusBadge,
  formatTimestamp,
} from '../platform'
import { ApiError, api } from '../api'
import type { Settings } from '../api'
import {
  ASK,
  FORWARD,
  LOOKUP,
  SHADOW,
  TEST_CALL,
  UNBILLED,
  PRE_AUTHORIZATION,
  askFailedLine,
  connectionsFrom,
  healthTestLine,
  keyTestLine,
  syncLine,
  type Connection,
  type ConnectionId,
  type HealthTest,
  type KeyTest,
  type StationListSync,
  type TestLine,
} from './connections'
import { dialInWords, needsAttention, type DialResult } from './dialReading'
import { tokenProblem, urlProblem } from './fieldRules'
import { keyAwaitsACall } from './keyPresented'
import { laneAppearance, type LaneStatus } from './laneAppearance'
import { observationAge, useObservationClock } from './observationAge'

const useStyles = makeStyles({
  cards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
    gap: tokens.spacingHorizontalM,
    alignItems: 'start',
  },
  section: {
    gridColumn: '1 / -1',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  columns: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: tokens.spacingHorizontalXL,
    alignItems: 'start',
  },
  stack: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  valued: {
    display: 'inline-flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
    marginTop: tokens.spacingVerticalS,
  },
  note: { color: tokens.colorNeutralForeground3 },
  failed: { color: tokens.colorStatusDangerForeground1 },
})

interface Props {
  data: Settings
  /** Reload the settings after a change. */
  onChanged: () => Promise<unknown>
}

interface LaneNow {
  status: LaneStatus
  label: string
  detail: string | null
  observedAt: number
}

/** The service's own sentence for a refusal, which is in `detail`. */
function refusal(e: unknown): string {
  if (e instanceof ApiError) return e.problem.detail || e.message
  return e instanceof Error ? e.message : 'The request failed.'
}

const what = (connection: Connection) =>
  connection.key?.label === 'Bearer token' ? 'token' : 'key'

export default function SettingsConnections({ data, onChanged }: Props) {
  const styles = useStyles()
  const now = useObservationClock()
  const connections = connectionsFrom(data)

  const [editing, setEditing] = useState<ConnectionId | null>(null)
  const [saved, setSaved] = useState<ConnectionId | null>(null)
  const [testing, setTesting] = useState<ConnectionId | null>(null)
  const [tested, setTested] = useState<Partial<Record<ConnectionId, TestLine>>>({})
  const [laneNow, setLaneNow] = useState<Record<string, LaneNow>>({})

  const edit = (connection: Connection) => {
    setSaved(null)
    setEditing(connection.id)
  }

  const afterSave = (connection: Connection) => {
    setEditing(null)
    setSaved(connection.id)
    // A result from before the change says nothing about the new values.
    setTested((t) => ({ ...t, [connection.id]: undefined }))
    return onChanged()
  }

  const test = async (connection: Connection) => {
    setTesting(connection.id)
    setSaved(null)
    try {
      let line: TestLine | null = null
      if (connection.lane && (connection.test === 'health' || connection.test === 'both')) {
        const lane = connection.lane.authority
        const health = await api.post<HealthTest & { observed_at?: number | null }>(
          `/authorities/${lane}/test`,
          {},
        )
        setLaneNow((seen) => ({
          ...seen,
          [lane]: {
            status: health.status as LaneStatus,
            label: `${health.status}, ${health.latency_ms} ms`,
            detail: health.detail,
            observedAt: health.observed_at ?? Date.now() / 1000,
          },
        }))
        line = healthTestLine(health)
      }
      if (connection.key && (connection.test === 'key' || connection.test === 'both')) {
        const found = await api.post<KeyTest>(
          `/credentials/${connection.key.relationship}/verify`,
          {},
        )
        line = keyTestLine(found, what(connection))
      }
      if (line) setTested((t) => ({ ...t, [connection.id]: line }))
    } catch (e) {
      setTested((t) => ({ ...t, [connection.id]: askFailedLine(refusal(e)) }))
    } finally {
      setTesting(null)
    }
  }

  const canTest = (connection: Connection) =>
    (connection.test === 'health' && Boolean(connection.lane)) ||
    (connection.test === 'key' && Boolean(connection.key)) ||
    (connection.test === 'both' && Boolean(connection.lane || connection.key))

  const canEdit = (connection: Connection) =>
    Boolean(connection.url?.address || connection.key || connection.link)

  const rows = (connection: Connection) => {
    const lane = connection.lane
    const seen = lane ? laneNow[lane.authority] : undefined
    const age = lane ? observationAge(seen?.observedAt ?? lane.observed_at, now) : null
    const reported = lane && lane.testable !== false
    const result = tested[connection.id]
    const awaiting =
      connection.key && lane
        ? keyAwaitsACall({
            updatedAt: connection.key.updatedAt,
            lastCallAt: lane.last_call_at,
            // Only a test the connection answered with the key in hand.
            proved: Boolean(result?.presented),
            what: what(connection),
            testPresentsIt: connection.test !== 'health',
          })
        : null
    const shown = lane ? laneAppearance({ status: lane.status, detail: lane.status_detail }, seen) : null

    return (
      <div className={styles.stack}>
        <KeyValueList divided>
          {connection.url ? (
            <KeyValueRow
              label="Base URL"
              value={
                <Valued source={connection.url.source}>
                  <MonoText>{connection.url.value || 'not set'}</MonoText>
                </Valued>
              }
            />
          ) : null}
          {connection.key ? (
            <KeyValueRow
              label={connection.key.label}
              value={
                <Valued
                  source={
                    connection.key.updatedAt
                      ? `${connection.key.source}, changed ${formatTimestamp(connection.key.updatedAt)}`
                      : connection.key.source
                  }
                >
                  {connection.key.isSet ? 'set' : 'not set'}
                </Valued>
              }
            />
          ) : null}
          {connection.link ? (
            <KeyValueRow
              label={connection.link.label}
              value={<MonoText>{connection.link.value || 'not set'}</MonoText>}
            />
          ) : null}
          {reported && lane ? (
            <KeyValueRow label="Timeout" value={`${lane.timeout_ms} ms`} />
          ) : null}
          {reported && lane ? (
            <KeyValueRow
              label="Recent calls"
              value={lane.recent_calls ? String(lane.recent_calls) : 'none'}
            />
          ) : null}
          {reported && age ? <KeyValueRow label="Status checked" value={age.label} /> : null}
        </KeyValueList>

        {reported && shown?.detail ? (
          <Caption1 className={styles.note}>{shown.detail}</Caption1>
        ) : null}
        {connection.note ? <Caption1 className={styles.note}>{connection.note}</Caption1> : null}
        {awaiting ? <Caption1 className={styles.note}>{awaiting}</Caption1> : null}

        {canTest(connection) || canEdit(connection) ? (
          <div className={styles.actions}>
            {canTest(connection) ? (
              <Button
                onClick={() => void test(connection)}
                disabled={testing === connection.id}
              >
                {testing === connection.id ? 'Testing' : 'Test'}
              </Button>
            ) : null}
            {canEdit(connection) ? (
              <Button onClick={() => edit(connection)}>Edit</Button>
            ) : null}
          </div>
        ) : null}
        {result ? (
          <Caption1 role="status" className={result.ok ? styles.note : styles.failed}>
            {result.line}
          </Caption1>
        ) : null}
        {saved === connection.id ? (
          <Caption1 role="status" className={styles.note}>
            Saved. In use from the next call.
          </Caption1>
        ) : null}
        {connection.id === 'hub_assets' ? <StationListSyncRow /> : null}
      </div>
    )
  }

  const card = (connection: Connection) => {
    const isEditing = editing === connection.id
    const title = isEditing ? `Edit: ${connection.name}` : connection.name
    const body = isEditing ? (
      <ConnectionEditor
        connection={connection}
        onSaved={() => afterSave(connection)}
        onCancel={() => setEditing(null)}
      />
    ) : (
      rows(connection)
    )
    if (connection.id === 'hubject') {
      return (
        <SectionCard key={connection.id} title={title}>
          <div className={styles.columns}>
            {body}
            <HubjectOptions data={data} onChanged={onChanged} />
          </div>
        </SectionCard>
      )
    }
    if (connection.id === 'ocpi') {
      return (
        <SectionCard key={connection.id} title={title} action={badge(connection)}>
          <div className={styles.stack}>
            {body}
            {isEditing ? null : <OcpiOptions data={data} onChanged={onChanged} />}
          </div>
        </SectionCard>
      )
    }
    return (
      <SectionCard key={connection.id} title={title} action={badge(connection)}>
        {body}
      </SectionCard>
    )
  }

  const badge = (connection: Connection) => {
    const lane = connection.lane
    if (!lane || lane.testable === false) return undefined
    const shown = laneAppearance(
      { status: lane.status, detail: lane.status_detail },
      laneNow[lane.authority],
    )
    return <StatusBadge status={shown.tone} label={shown.label} />
  }

  return (
    <div className={styles.section}>
      <SectionLabel>Connections</SectionLabel>
      {/* Three rows: the two authorities, the Hubject connector across the
          width with its options beside it, then the Integration Hub. One
          grid with a full-width card in the middle leaves an empty column
          beside the first two. */}
      <div className={styles.cards}>{connections.filter(before).map(card)}</div>
      {connections.filter((c) => c.id === 'hubject').map(card)}
      <div className={styles.cards}>{connections.filter(after).map(card)}</div>
    </div>
  )
}

const before = (connection: Connection) => connection.id === 'tim' || connection.id === 'ocpi'
const after = (connection: Connection) => connection.id.startsWith('hub_')

/**
 * The edit form of one connection.
 *
 * ITS OWN COMPONENT SO THE KEY DIES WITH THE FORM. What is typed lives in
 * this component's state and nowhere else, so Save and Cancel both end it by
 * unmounting, with no reset anybody has to remember to write.
 */
function ConnectionEditor({
  connection,
  onSaved,
  onCancel,
}: {
  connection: Connection
  onSaved: () => Promise<unknown>
  onCancel: () => void
}) {
  const styles = useStyles()
  const [form, setForm] = useState({
    url: connection.url?.address ? connection.url.value : '',
    key: '',
    link: connection.link?.value ?? '',
  })
  const [saving, setSaving] = useState(false)
  const [refused, setRefused] = useState<string | null>(null)

  const urlSaid = connection.url?.address ? urlProblem(form.url, connection.key?.label) : null
  const linkSaid = connection.link ? urlProblem(form.link) : null
  const keySaid = connection.key?.label === 'Bearer token' ? tokenProblem(form.key) : null

  const save = () => {
    // Only what changed is sent, so a save that touched the key does not
    // rewrite the address, and each written field is its own audit row.
    const body: Record<string, unknown> = {}
    if (connection.url?.address && form.url.trim() !== connection.url.value) {
      body.addresses = { [connection.url.address]: form.url.trim() }
    }
    if (connection.key && form.key) {
      body.credentials = { [connection.key.relationship]: form.key }
    }
    if (connection.link && form.link.trim() !== connection.link.value) {
      body[connection.link.setting] = form.link.trim()
    }
    if (Object.keys(body).length === 0) {
      onCancel()
      return
    }
    setSaving(true)
    setRefused(null)
    api
      .put('/settings', body)
      .then(() => onSaved())
      .catch((e) => {
        // The form stays, with the service's reason in it.
        setRefused(refusal(e))
        setSaving(false)
      })
  }

  return (
    <div className={styles.stack}>
      {connection.url ? (
        <Field
          label="Base URL"
          hint={
            connection.url.address
              ? `${connection.url.help} Leave empty to use the deployment's value. Example: ${connection.url.example}`
              : connection.url.help
          }
          validationMessage={urlSaid ?? undefined}
        >
          {connection.url.address ? (
            <Input value={form.url} onChange={(_, d) => setForm((f) => ({ ...f, url: d.value }))} />
          ) : (
            <Input value={connection.url.value} readOnly />
          )}
        </Field>
      ) : null}
      {connection.key ? (
        <Field
          label={connection.key.label}
          hint={connection.key.help}
          validationMessage={keySaid ?? undefined}
        >
          <Input
            type="password"
            autoComplete="off"
            value={form.key}
            onChange={(_, d) => setForm((f) => ({ ...f, key: d.value }))}
          />
        </Field>
      ) : null}
      {connection.link ? (
        <Field
          label={connection.link.label}
          hint={connection.link.help}
          validationMessage={linkSaid ?? undefined}
        >
          <Input value={form.link} onChange={(_, d) => setForm((f) => ({ ...f, link: d.value }))} />
        </Field>
      ) : null}
      {refused ? (
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>Not saved</MessageBarTitle>
            {refused}
          </MessageBarBody>
        </MessageBar>
      ) : null}
      <div className={styles.actions}>
        <Button
          appearance="primary"
          disabled={saving || Boolean(urlSaid) || Boolean(linkSaid) || Boolean(keySaid)}
          onClick={save}
        >
          Save
        </Button>
        <Button onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  )
}

/** How often the card asks whether a running sync has ended. */
export const syncPolling = { everyMs: 2000 }

/**
 * Sync now, and the last sync in one line.
 *
 * The timed pull runs every fifteen minutes. This starts one now. The service
 * answers at once and the pull goes on, so the card asks for the state until
 * it says the pull has ended.
 */
function StationListSyncRow() {
  const styles = useStyles()
  const [state, setState] = useState<StationListSync | null>(null)
  const [refused, setRefused] = useState<string | null>(null)
  const [starting, setStarting] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const alive = useRef(true)

  const read = useCallback(
    () =>
      api.get<StationListSync>('/catalogue/sync').then((found) => {
        if (alive.current) setState(found)
        return found
      }),
    [],
  )

  const watch = useCallback(() => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      read()
        .then((found) => {
          if (found.running && alive.current) watch()
        })
        // A read that fails leaves the last line standing. It is retried by
        // the next press.
        .catch(() => undefined)
    }, syncPolling.everyMs)
  }, [read])

  useEffect(() => {
    alive.current = true
    read()
      .then((found) => {
        if (found.running) watch()
      })
      // A service older than this console has no such endpoint. No row then.
      .catch(() => undefined)
    return () => {
      alive.current = false
      if (timer.current) clearTimeout(timer.current)
    }
  }, [read, watch])

  const start = () => {
    setStarting(true)
    setRefused(null)
    api
      .post('/catalogue/sync', {})
      .then(() => setState((held) => (held ? { ...held, running: true } : held)))
      .catch((e) => setRefused(refusal(e)))
      .finally(() => {
        setStarting(false)
        // Started or refused because one is running: either way there is a
        // pull to wait for.
        watch()
      })
  }

  if (!state) return null
  const last = syncLine(state)
  return (
    <div className={styles.stack}>
      <div className={styles.actions}>
        <Button onClick={start} disabled={starting || state.running || !state.configured}>
          Sync now
        </Button>
        <Caption1 role="status" className={last.ok ? styles.note : styles.failed}>
          {state.configured ? last.line : 'Set the Base URL first.'}
        </Caption1>
      </div>
      {refused ? <Caption1 className={styles.failed}>{refused}</Caption1> : null}
    </div>
  )
}

/** A value with where it comes from as a small line under it. */
export function Valued({ children, source }: { children: ReactNode; source: string }) {
  const styles = useStyles()
  return (
    <span className={styles.valued}>
      <span>{children}</span>
      <Caption1 className={styles.note}>Source: {source}</Caption1>
    </span>
  )
}

const OPTIONS: { name: string; label: string; help: string }[] = [
  {
    name: SHADOW,
    label: 'Shadow mode (record only, no call)',
    help: 'Records what would have been sent to Hubject. Nothing is called.',
  },
  {
    name: LOOKUP,
    label: 'Check the DCS token table first',
    help: 'A card found in the DCS token table is not sent to Hubject.',
  },
  {
    name: TEST_CALL,
    label: 'Allow a single test call',
    help: 'Shows the Test call form below.',
  },
]

/**
 * Service OCPI's switches: whether a start or stop nobody owns is kept here
 * rather than forwarded to it (DEC-25), and whether it may ask for a
 * pre-authorization before a remote start (DEC-27).
 *
 * Written straight away, like the Hubject options: off is the behaviour
 * before either existed. Each is shown only when the service offers it.
 */
const OCPI_SWITCHES = [
  {
    name: UNBILLED,
    label: 'Unowned session events',
    hint: 'On: a start or stop nobody owns stays in this service and is not forwarded.',
  },
  {
    name: PRE_AUTHORIZATION,
    label: 'Pre-authorization',
    hint: 'On: Service OCPI may ask whether a card would be approved before a remote start. Nothing is opened.',
  },
]

function OcpiOptions({ data, onChanged }: Props) {
  const styles = useStyles()
  const offered = OCPI_SWITCHES.flatMap((declared) => {
    const state = (data.switches ?? []).find((item) => item.name === declared.name)
    return state ? [{ ...declared, state }] : []
  })
  const [writing, setWriting] = useState(false)
  const [refused, setRefused] = useState<string | null>(null)

  if (offered.length === 0) return null

  const write = (name: string, on: boolean) => {
    setWriting(true)
    setRefused(null)
    api
      .put('/settings', { switches: { [name]: on } })
      .then(() => onChanged())
      .catch((e) => setRefused(refusal(e)))
      .finally(() => setWriting(false))
  }

  return (
    <div className={styles.stack}>
      {offered.map(({ name, label, hint, state }) => (
        <Field
          key={name}
          hint={`${hint} Source: ${state.updated_at ? 'console' : 'deployment'}.`}
        >
          <Switch
            checked={Boolean(state.on)}
            disabled={writing}
            onChange={(_, d) => write(name, d.checked)}
            label={label}
          />
        </Field>
      ))}
      {refused ? (
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>Not changed</MessageBarTitle>
            {refused}
          </MessageBarBody>
        </MessageBar>
      ) : null}
    </div>
  )
}

/**
 * DEC-26: the providers whose Plug and Charge contracts the connector is never
 * asked about. The connector's own list, pasted as its BMW Estate page shows
 * it; the service checks every entry and names the one it cannot read.
 */
function DcsContractProviders({
  saved,
  onChanged,
}: {
  saved: string
  onChanged: () => Promise<unknown>
}) {
  const styles = useStyles()
  const [value, setValue] = useState(saved)
  const [writing, setWriting] = useState(false)
  const [refused, setRefused] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  const save = () => {
    setWriting(true)
    setRefused(null)
    setDone(false)
    api
      .put('/settings', { dcs_contract_providers: value })
      .then(() => {
        setDone(true)
        return onChanged()
      })
      .catch((e) => setRefused(refusal(e)))
      .finally(() => setWriting(false))
  }

  return (
    <div className={styles.stack}>
      <Field
        label="DCS contract providers"
        hint="Plug and Charge contracts of these providers are never sent to the connector. Paste the list from its BMW Estate page, for example DE*DCS, AT*DCS."
        validationMessage={refused ?? undefined}
      >
        <Input value={value} onChange={(_, d) => setValue(d.value)} />
      </Field>
      <div className={styles.actions}>
        <Button disabled={writing || value === saved} onClick={save}>
          {writing ? 'Saving' : 'Save providers'}
        </Button>
      </div>
      {done ? (
        <Caption1 role="status" className={styles.note}>
          Saved. In use from the next call.
        </Caption1>
      ) : null}
    </div>
  )
}

type Asked = 'enable' | 'disable' | 'stop-forwarding'

/** The Hubject connector's switches and its test call. */
function HubjectOptions({ data, onChanged }: Props) {
  const styles = useStyles()
  const switches = data.switches ?? []
  const state = (name: string) => switches.find((item) => item.name === name)
  const on = (name: string) => Boolean(state(name)?.on)
  const source = (name: string) => (state(name)?.updated_at ? 'console' : 'deployment')

  const [asked, setAsked] = useState<Asked | null>(null)
  const [writing, setWriting] = useState(false)
  const [refused, setRefused] = useState<string | null>(null)
  const [dial, setDial] = useState({ station_id: '', evse_id: '', token: '' })
  const [dialling, setDialling] = useState(false)
  const [dialled, setDialled] = useState<DialResult | null>(null)

  const write = (flips: Record<string, boolean>) => {
    setWriting(true)
    setRefused(null)
    api
      .put('/settings', { switches: flips })
      .then(() => onChanged())
      // The rule lives in the service. This shows what it said.
      .catch((e) => setRefused(refusal(e)))
      .finally(() => {
        setWriting(false)
        setAsked(null)
      })
  }

  // ENABLED IS ASKING. Turning it on turns forwarding on with it, in one
  // request the service writes in the safe order. Turning it off stops asking
  // and leaves forwarding on: with forwarding off, the stop of a session
  // Hubject already holds is not sent, and that session stays open there.
  const confirmed = () => {
    if (asked === 'enable') write({ [FORWARD]: true, [ASK]: true })
    if (asked === 'disable') write({ [ASK]: false })
    if (asked === 'stop-forwarding') write({ [FORWARD]: false })
  }

  const question: Record<Asked, string> = {
    enable: on(LOOKUP)
      ? 'Cards outside the DCS table at public stations will be sent to Hubject. Continue?'
      : 'Every card will be sent to both DCS and Hubject. Continue?',
    disable:
      'No card will be sent to Hubject. Stops of sessions Hubject already holds are still forwarded. Continue?',
    'stop-forwarding':
      'Stops of sessions Hubject still holds will not be sent to it. Continue?',
  }

  const dialOnce = () => {
    setDialling(true)
    setDialled(null)
    api
      .post<DialResult>('/hubject/dial-once', dial)
      .then((r) => {
        setDialled(r)
        // Cleared, so the card is not left in a field on a shared screen.
        setDial((d) => ({ ...d, token: '' }))
      })
      .catch((e) => setDialled({ error: refusal(e) } as DialResult))
      .finally(() => setDialling(false))
  }

  if (!state(ASK)) return null

  return (
    <div className={styles.stack}>
      <Field hint={`Sends cards to Hubject. Source: ${source(ASK)}.`}>
        <Switch
          checked={on(ASK)}
          disabled={writing || asked !== null}
          onChange={(_, d) => setAsked(d.checked ? 'enable' : 'disable')}
          label="Enabled"
        />
      </Field>
      {asked ? (
        <MessageBar intent="warning">
          <MessageBarBody>{question[asked]}</MessageBarBody>
          <MessageBarActions>
            <Button appearance="primary" disabled={writing} onClick={confirmed}>
              Continue
            </Button>
            <Button disabled={writing} onClick={() => setAsked(null)}>
              Cancel
            </Button>
          </MessageBarActions>
        </MessageBar>
      ) : null}
      {on(ASK) && !on(LOOKUP) ? (
        <Caption1 className={styles.note}>Every card is sent to both DCS and Hubject.</Caption1>
      ) : null}
      {!on(ASK) && on(FORWARD) ? (
        <div className={styles.stack}>
          <Caption1 className={styles.note}>
            Not asking Hubject. Stops of sessions Hubject already holds are still forwarded.
          </Caption1>
          <div>
            <Button
              size="small"
              disabled={writing || asked !== null}
              onClick={() => setAsked('stop-forwarding')}
            >
              Stop forwarding
            </Button>
          </div>
        </div>
      ) : null}

      {OPTIONS.filter((option) => state(option.name)).map((option) => (
        <Field key={option.name} hint={`${option.help} Source: ${source(option.name)}.`}>
          <Switch
            checked={on(option.name)}
            disabled={writing}
            onChange={(_, d) => write({ [option.name]: d.checked })}
            label={option.label}
          />
        </Field>
      ))}
      {refused ? (
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>Not changed</MessageBarTitle>
            {refused}
          </MessageBarBody>
        </MessageBar>
      ) : null}

      <DcsContractProviders
        saved={data.dcs_contract_providers ?? ''}
        onChanged={onChanged}
      />

      {on(TEST_CALL) ? (
        <div className={styles.stack}>
          <Body1Strong>Test call</Body1Strong>
          <Caption1 className={styles.note}>
            One authorization at Hubject for the card below, released straight away.
          </Caption1>
          <Field label="Station ID" hint="The charge point's ID, as the OCPP Server sends it. Example: 20BZ0221A">
            <Input
              value={dial.station_id}
              onChange={(_, d) => setDial((v) => ({ ...v, station_id: d.value }))}
            />
          </Field>
          <Field label="EVSE ID" hint="The ID of one socket, not of the station. Example: DE*WLE*E20BZ0221A1">
            <Input
              value={dial.evse_id}
              onChange={(_, d) => setDial((v) => ({ ...v, evse_id: d.value }))}
            />
          </Field>
          <Field label="Card" hint="The card's UID. Not stored, not shown back, cleared after the call.">
            <Input
              type="password"
              autoComplete="off"
              value={dial.token}
              onChange={(_, d) => setDial((v) => ({ ...v, token: d.value }))}
            />
          </Field>
          <div className={styles.actions}>
            <Button
              appearance="primary"
              disabled={dialling || !dial.station_id || !dial.evse_id || !dial.token}
              onClick={dialOnce}
            >
              {dialling ? 'Calling' : 'Send test call'}
            </Button>
          </div>
          {dialled ? (
            <MessageBar intent={needsAttention(dialled) ? 'error' : 'info'}>
              <MessageBarBody>
                <MessageBarTitle>
                  {needsAttention(dialled) ? 'A session is still open at Hubject' : 'Result'}
                </MessageBarTitle>
                {dialInWords(dialled)}
              </MessageBarBody>
            </MessageBar>
          ) : null}
          {dialled?.authorize ? (
            <KeyValueList divided>
              <KeyValueRow label="Answer" value={dialled.authorize.status ?? 'none'} />
              <KeyValueRow
                label="Round trip"
                value={`${dialled.authorize.elapsed_ms} ms of ${dialled.budget_ms} ms`}
              />
              <KeyValueRow
                label="Release"
                value={
                  dialled.released?.needed
                    ? `${dialled.released.status ?? 'no answer'}, ${dialled.released.elapsed_ms} ms`
                    : `not needed: ${dialled.released?.why_not ?? ''}`
                }
              />
            </KeyValueList>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
